/**
 * Widget gallery — a one-file "Storybook" for the generative-UI catalog.
 *
 * Renders every catalog widget from a fixture of sample specs through the REAL
 * JsonRenderer, so it can't drift from what agents actually emit in chat. Theme is
 * driven by ?theme=light|dark (so Playwright can screenshot each theme cleanly).
 * Mounted at /spike/gallery. Not part of the product surface.
 */
import React, { useEffect } from 'react';
import { JsonRenderer } from './JsonRenderer';
import { UISpec } from './uiSpec';

// Deliberately simple, everyday examples — this is a general thinking/chat toolkit
// (research, decisions, brainstorm, summarise), NOT a project-management app. Each
// sample shows the widget's shape with universal content, no domain jargon.
const SAMPLES: Array<{ id: string; label: string; spec: UISpec }> = [
  { id: 'text', label: 'Text', spec: { type: 'Text', props: { text: 'A short, plain-language answer, right inside the conversation.' } } },
  { id: 'bullets', label: 'Bullets', spec: { type: 'Bullets', props: { title: 'Key points', items: ['Cheaper than the alternatives', 'Works offline', 'Takes about a week to set up'] } } },
  { id: 'select', label: 'Select — single', spec: { type: 'Select', props: { label: 'Which do you prefer?', action: 'pick', options: [{ label: 'Option A', value: 'a' }, { label: 'Option B', value: 'b' }, { label: 'Option C', value: 'c' }] } } },
  { id: 'select-multi', label: 'Select — multi', spec: { type: 'Select', props: { label: 'Pick any that matter to you', multiple: true, action: 'pick', options: [{ label: 'Cost', value: 'cost' }, { label: 'Quality', value: 'quality' }, { label: 'Speed', value: 'speed' }] } } },
  { id: 'table', label: 'Table', spec: { type: 'Table', props: { title: 'At a glance', columns: ['Option', 'Pros', 'Cons'], rows: [['Option A', 'Cheaper', 'Slower'], ['Option B', 'Faster', 'Pricier']] } } },
  { id: 'bar', label: 'Bar chart', spec: { type: 'BarChart', props: { title: 'Responses by option', xKey: 'name', bars: [{ key: 'count', label: 'Responses' }], data: [{ name: 'A', count: 18 }, { name: 'B', count: 12 }, { name: 'C', count: 7 }] } } },
  { id: 'line', label: 'Line chart', spec: { type: 'LineChart', props: { title: 'Trend over time', xKey: 'month', lines: [{ key: 'value', label: 'Value' }], data: [{ month: 'Jan', value: 8 }, { month: 'Feb', value: 14 }, { month: 'Mar', value: 11 }] } } },
  { id: 'pie', label: 'Pie chart', spec: { type: 'PieChart', props: { title: 'Share of the whole', data: [{ name: 'A', value: 50 }, { name: 'B', value: 30 }, { name: 'C', value: 20 }] } } },
  { id: 'scatter', label: 'Scatter chart', spec: { type: 'ScatterChart', props: { title: 'Two variables', points: [{ x: 2, y: 8 }, { x: 5, y: 5 }, { x: 8, y: 9 }] } } },
  { id: 'distribution', label: 'Distribution (bell curve)', spec: { type: 'Distribution', props: { title: 'Likely outcome', xLabel: 'value', series: [{ label: 'Estimate', mean: 10, stdDev: 2 }], markers: [{ x: 13, label: 'target' }] } } },
  { id: 'button', label: 'Button', spec: { type: 'Button', props: { label: 'Show me', action: 'go', variant: 'primary' } } },
  // The composition agents emit on data turns: a chart, then follow-up "tap to ask" chips.
  { id: 'chart-offer', label: 'Chart + follow-up chips', spec: { type: 'Stack', children: [
    { type: 'LineChart', props: { title: 'Trend over time', xKey: 'month', lines: [{ key: 'value', label: 'Value' }], data: [{ month: 'Jan', value: 8 }, { month: 'Feb', value: 14 }, { month: 'Mar', value: 11 }] } },
    { type: 'Select', props: { label: 'Want to dig in?', action: 'deepdive', options: [{ label: 'Spread', value: 'spread' }, { label: 'Sectors', value: 'sectors' }] } },
  ] } },
];

const noop = () => {};

const WidgetCard: React.FC<{ id: string; label: string; spec: UISpec }> = ({ id, label, spec }) => (
  <div data-testid={`widget-${id}`} className="rounded-xl border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 p-4">
    <div className="mb-3 text-[11px] font-medium uppercase tracking-wide text-secondary-400">{label}</div>
    <JsonRenderer spec={spec} onAction={noop} />
  </div>
);

const GalleryPage: React.FC = () => {
  const theme = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('theme') === 'dark' ? 'dark' : 'light';

  useEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains('dark');
    if (theme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
    return () => { if (had) root.classList.add('dark'); else root.classList.remove('dark'); };
  }, [theme]);

  return (
    <div data-testid="widget-gallery" data-theme={theme} className="min-h-screen bg-white dark:bg-black p-6">
      <h1 className="mb-5 text-lg font-semibold text-secondary-900 dark:text-white">Generative-UI catalog · {theme}</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
        {SAMPLES.map((s) => <WidgetCard key={s.id} {...s} />)}
      </div>
    </div>
  );
};

export default GalleryPage;
